import { parseAirportLocalOrUTC, parseFlightDate } from './utils';

import type { NetSkyscannerArrivaldeparturesvcV1ArrivalDepartureService as ArrivalDepartureTypes } from '@skyscanner-internal/arrival-departure-svc-web-http-client';

type FlightStatusUpdate = ArrivalDepartureTypes.FlightStatusUpdate;

/**
 * Self-contained active-flight selection engine for the flight tracker.
 *
 * This module owns the single question "given a set of flight instances and a
 * moment in time, which instance should be shown?" and nothing else - no
 * React, no DOM, no hash parsing. The component layer decides *which date* is
 * in scope and *what reference time* to pass; this module decides *which
 * flight within that scope*. Keeping the two apart is what stops the
 * cross-date default-selection bug (CASTLE-5111) from creeping back: the
 * default selector here can only ever see the flights it is handed.
 *
 * Two entry points, one per selection mode:
 *  - `selectDefaultFlight` - no explicit user/hash choice. Applies the
 *    product priority ladder (CASTLE-5111) scoped to a single date.
 *  - `getSelectedFlight` - an explicit deep-link hash (date + time) is
 *    present. Resolves the exact instance the hash names, falling back to a
 *    time-relative match within the hashed scope.
 */

const TWO_HOURS_MS = 2 * 60 * 60 * 1000;

/**
 * Best-known landing instant (real UTC): actual, then estimated, then
 * scheduled arrival.
 */
export const getLandingTime = (flight: FlightStatusUpdate) =>
  parseFlightDate(
    flight.actualArrivalTime ||
      flight.estimatedArrivalTime ||
      flight.scheduledArrivalTime,
  );

/**
 * Best-known departure instant (real UTC): estimated, then scheduled.
 */
export const getDepartureTime = (flight: FlightStatusUpdate) =>
  parseFlightDate(
    flight.estimatedDepartureTime || flight.scheduledDepartureTime,
  );

/**
 * The instant a flight sits at on the timeline for ordering purposes:
 * departure first (estimated then scheduled), arrival only as a last resort
 * for rows that expose no departure time at all.
 */
export const getFlightTimelineTime = (flight: FlightStatusUpdate) =>
  parseFlightDate(
    flight.estimatedDepartureTime ||
      flight.scheduledDepartureTime ||
      flight.estimatedArrivalTime ||
      flight.scheduledArrivalTime,
  );

/**
 * Scheduled departure parsed with the airport-local wall clock carried in the
 * Date's UTC parts (see `parseAirportLocalOrUTC`). Used both for exact
 * hash-time matching and by the component's hash encoder, so it is exported.
 */
export const getLocalisedScheduledDeparture = (flight: FlightStatusUpdate) =>
  parseAirportLocalOrUTC(
    flight.localisedScheduledDepartureTime || flight.scheduledDepartureTime,
  );

/**
 * Airport-local vs UTC offset (ms) sampled from the first flight exposing both
 * the localised and UTC scheduled-departure fields. All instances of one
 * flight code share a departure airport, so one sample suffices. Returns null
 * for legacy payloads with no localised field; callers then treat the hash as
 * UTC (pre-CASTLE-4926 behaviour).
 */
export const getAirportOffsetMs = (
  flights: FlightStatusUpdate[],
): number | null => {
  for (const flight of flights) {
    const localised = parseAirportLocalOrUTC(
      flight.localisedScheduledDepartureTime,
    );
    const utc = parseAirportLocalOrUTC(flight.scheduledDepartureTime);
    if (localised && utc) {
      return localised.getTime() - utc.getTime();
    }
  }
  return null;
};

/**
 * Sort flights ascending by timeline time (earliest departure first). Stable
 * for equal times; does not mutate the input.
 */
export const sortFlightsByTime = (flights: FlightStatusUpdate[]) =>
  [...flights].sort((leftFlight, rightFlight) => {
    const leftTime = getFlightTimelineTime(leftFlight)?.getTime() ?? 0;
    const rightTime = getFlightTimelineTime(rightFlight)?.getTime() ?? 0;

    return leftTime - rightTime;
  });

/**
 * A flight is "active" at `referenceTime` when the moment sits inside the
 * 2-hour window that opens at landing: landing <= reference <= landing + 2h.
 * Covers a just-landed flight; the airborne case (landing still in the
 * future) is handled separately by the default ladder's in-air step.
 */
export const isFlightActive = (
  flight: FlightStatusUpdate,
  referenceTime: Date,
) => {
  const landingTime = getLandingTime(flight);

  if (!landingTime) {
    return false;
  }

  const activeWindowEnd = landingTime.getTime() + TWO_HOURS_MS;

  return (
    referenceTime.getTime() >= landingTime.getTime() &&
    referenceTime.getTime() <= activeWindowEnd
  );
};

const hasSameMinuteTimestamp = (date: Date, target: Date) =>
  date.getUTCHours() === target.getUTCHours() &&
  date.getUTCMinutes() === target.getUTCMinutes();

/**
 * Default active-flight selection (CASTLE-5111).
 *
 * Applies the product priority ladder to a set of flights that the caller has
 * ALREADY scoped to a single date tab. Because the input is one date's
 * flights, the result can never cross a date boundary - that scoping is the
 * fix for tomorrow's instance winning over today's active one.
 *
 * Ladder, stopping at the first rung that matches:
 *  1. Next to depart - the earliest flight whose departure is still in the
 *     future relative to `referenceTime`.
 *  2. In the air - only when every flight has already departed: a flight that
 *     has departed but not yet landed (`departure <= now < landing`),
 *     whatever its arrival ETA. Ties broken by most-recently departed. In-air
 *     deliberately outranks a completed flight: an in-progress flight is the
 *     more relevant "active" one, so a still-airborne long-haul beats a
 *     sibling that has already finished (CASTLE-5111 review decision).
 *  3. Completed - the most-recently-arrived flight that has actually landed
 *     (`landing <= now`).
 *  4. Last resort - the last flight on the timeline, used only when no row
 *     exposes a landing time to rank by.
 *
 * The 2-hour active window (see `isFlightActive`) is deliberately NOT part of
 * this ladder: default selection turns purely on departed/in-air/landed
 * state. The 2h window is a deep-link concept only (`getSelectedFlight`).
 *
 * `referenceTime` is a real-UTC instant (the viewer's "now", or the hashed
 * date at the current time for a date-only hash). Returns null only for an
 * empty input.
 */
export const selectDefaultFlight = (
  flights: FlightStatusUpdate[],
  referenceTime: Date,
): FlightStatusUpdate | null => {
  if (flights.length === 0) {
    return null;
  }

  const now = referenceTime.getTime();
  const sortedFlights = sortFlightsByTime(flights);

  // Rung 1: next to depart. sortedFlights is ascending by departure, so the
  // first future departure is also the earliest.
  const nextToDepart = sortedFlights.find((flight) => {
    const departure = getDepartureTime(flight);
    return departure != null && departure.getTime() > now;
  });

  if (nextToDepart) {
    return nextToDepart;
  }

  // Rung 2: in the air. Every flight has departed (rung 1 found none in the
  // future). Keep those still airborne now - departed but not yet landed
  // (departure <= now < landing), any arrival ETA - then take the
  // most-recently-departed of them. A flight with no known landing time can't
  // be shown as in-air, so it drops through to the rung-4 fallback.
  const inAir = sortedFlights.filter((flight) => {
    const departure = getDepartureTime(flight);
    const landing = getLandingTime(flight);
    return (
      departure != null &&
      landing != null &&
      departure.getTime() <= now &&
      now < landing.getTime()
    );
  });

  if (inAir.length > 0) {
    return inAir.reduce((mostRecent, flight) => {
      const flightDeparture = getDepartureTime(flight)?.getTime() ?? 0;
      const bestDeparture = getDepartureTime(mostRecent)?.getTime() ?? 0;
      return flightDeparture >= bestDeparture ? flight : mostRecent;
    }, inAir[0]);
  }

  // Rung 3: completed - most recently arrived. Only flights that have actually
  // landed (landing <= now); a future arrival time is not an "arrival", so an
  // in-air flight can never win here (that's rung 2's job).
  const landed = sortedFlights.filter((flight) => {
    const landing = getLandingTime(flight);
    return landing != null && landing.getTime() <= now;
  });

  if (landed.length > 0) {
    return landed.reduce((mostRecent, flight) => {
      const flightLanding = getLandingTime(flight)?.getTime() ?? 0;
      const bestLanding = getLandingTime(mostRecent)?.getTime() ?? 0;
      return flightLanding >= bestLanding ? flight : mostRecent;
    }, landed[0]);
  }

  // Rung 4: no row exposes a usable landing time - last on the timeline.
  return sortedFlights.at(-1) ?? null;
};

/**
 * Explicit (deep-link) flight selection.
 *
 * Used when the URL hash carries a specific date + time. Resolves, in order:
 *  1. Exact airport-local scheduled-departure match for the hashed minute.
 *  2. The 2-hour post-landing active window, preferring a flight departing
 *     inside that same window over the recently-landed one.
 *  3. The next flight whose timeline time is at or after the reference.
 *  4. The last flight of the (already date-scoped) set.
 *
 * `effectiveDateTime` is airport-local when `hasExplicitTime`; `airportOffsetMs`
 * translates it back to real UTC for the absolute-moment comparisons in rungs
 * 2 and 3. Returns null only for an empty input.
 */
export const getSelectedFlight = (
  flights: FlightStatusUpdate[],
  effectiveDateTime: Date,
  hasExplicitTime: boolean,
  airportOffsetMs: number | null,
) => {
  if (flights.length === 0) {
    return null;
  }

  const sortedFlights = sortFlightsByTime(flights);

  // Rule 1: exact airport-local departure match for an explicit hash time.
  if (hasExplicitTime) {
    const explicitlySelectedFlight = sortedFlights.find((flight) => {
      const departureTime = getLocalisedScheduledDeparture(flight);

      return departureTime
        ? hasSameMinuteTimestamp(departureTime, effectiveDateTime)
        : false;
    });

    if (explicitlySelectedFlight) {
      return explicitlySelectedFlight;
    }
  }

  // Translate the airport-local hash back to real UTC for absolute-moment
  // comparisons. Constructing from a milliseconds-since-epoch number carries
  // no timezone ambiguity, so the no-new-date-with-args rule does not apply.
  const effectiveUTC =
    hasExplicitTime && airportOffsetMs != null
      ? // eslint-disable-next-line skyscanner-dates/no-new-date-with-args
        new Date(effectiveDateTime.getTime() - airportOffsetMs)
      : effectiveDateTime;

  // Rule 2 & 3: 2-hour post-landing active window.
  const activeFlight = sortedFlights.find((flight) =>
    isFlightActive(flight, effectiveUTC),
  );

  if (activeFlight) {
    // Rule 2: prefer a flight departing within the same active window over the
    // recently-landed one.
    const activeLandingTime = getLandingTime(activeFlight);

    if (activeLandingTime) {
      const activeWindowEnd = activeLandingTime.getTime() + TWO_HOURS_MS;

      const conflictingFlight = sortedFlights.find((flight) => {
        if (flight === activeFlight) return false;

        const depTime = getDepartureTime(flight);

        return (
          depTime != null &&
          depTime.getTime() >= activeLandingTime.getTime() &&
          depTime.getTime() <= activeWindowEnd
        );
      });

      if (conflictingFlight) {
        return conflictingFlight;
      }
    }

    // Rule 3: no departing conflict - return the active flight.
    return activeFlight;
  }

  const nextFlight = sortedFlights.find((flight) => {
    const timelineTime = getFlightTimelineTime(flight);

    return timelineTime
      ? timelineTime.getTime() >= effectiveUTC.getTime()
      : false;
  });

  if (nextFlight) {
    return nextFlight;
  }

  return sortedFlights.at(-1) || null;
};
